import { Button } from '../../../components/buttons'
import { colorPalette } from '../../../style/colorPalette'
import ReloadIcon from '../../../components/icons/ReloadIcon'
import LockOpenIcon from '../../../components/icons/LockOpenIcon'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { MonthSelect } from '../../../components/toolbars/common'
import { Divider, IconButton, styled, Typography } from '@mui/material'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import { useCallback, useEffect, useState } from 'react'
import DateVO from '../../../../vo/DateVO'
import { intl } from '../../../../i18n'
import { ColDef } from 'ag-grid-community'
import { SortedColumnState } from '../../../model/bulkSheetColumnSortState'
import FilterButton from '../../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../../containers/BulkSheetView/components/header/ColumnSettingButton'
import SettingSpeedDial from '../../../components/headers/HeaderBar/SettingSpeedDial'
import { IconGroup } from '../../../components/headers/HeaderBar/PageHeader'
import { DecimalAndSexagesimalScale } from '../../../../lib/functions/decimalAndSexagesimalScale'
import { DecimalAndSexagesimalScaleToggleButtonGroup } from '../../../components/inputs/ToggleButtonGroup/DecimalAndSexagesimalScaleToggleGroup'
import EndDateSelector, { EndDatePreset } from './EndDateSelector'
import { FontWeight } from '../../../../styles/commonStyles'

export type ApprovalSummary = {
  unApproved: number
  unRequested: number
  actualWorkHourEmpty: number
  taskActualWorkEmpty: number
  workingTaskHoursMismatch: number
}

type Props = {
  loading: boolean
  refresh: () => void
  onMonthChange: (year: number, month: number) => void
  isClosing?: boolean
  closedAt?: string
  onClickProjectClosing: () => void
  onClickExport: () => void
  filteredColumns?: ColDef[]
  onDeleteFilteredColumn?: (column: ColDef) => void
  resetFilters?: () => void
  sortColumnsState?: SortedColumnState[]
  onDeleteSortedColumn?: (colId: string | ColDef<any>) => void
  onDeleteSortedAllColumns?: () => void
  onChangeSortColumnState?: (
    colId: string | ColDef<any>,
    sort: 'asc' | 'desc' | null
  ) => void
  onClickColumnSettingButton?: (e: any) => void
  columnSettingOpen?: boolean
  rowHeight: number
  onChangeRowHeight: (value: number) => void
  approvalSummary?: ApprovalSummary
  decimalScaleUnit: DecimalAndSexagesimalScale
  onChangeDecimalScaleUnit: (value: DecimalAndSexagesimalScale) => void
  endDate?: DateVO
  onEndDateChange?: (date: DateVO) => void
  isCurrentMonth?: boolean
  endDatePreset?: EndDatePreset
  setEndDatePreset?: (preset: EndDatePreset) => void
  displayMonth?: DateVO
}

const Header = ({
  loading,
  refresh,
  onMonthChange,
  isClosing,
  closedAt,
  onClickProjectClosing,
  onClickExport,
  filteredColumns = [],
  onDeleteFilteredColumn,
  resetFilters,
  sortColumnsState = [],
  onDeleteSortedColumn,
  onDeleteSortedAllColumns,
  onChangeSortColumnState,
  onClickColumnSettingButton,
  columnSettingOpen = false,
  rowHeight,
  onChangeRowHeight,
  approvalSummary,
  decimalScaleUnit,
  onChangeDecimalScaleUnit,
  endDate,
  onEndDateChange,
  isCurrentMonth = true,
  endDatePreset,
  setEndDatePreset,
  displayMonth,
}: Props) => {
  const now = DateVO.now()
  const [currentDate, setCurrentDate] = useState<DateVO>(displayMonth || now)

  useEffect(() => {
    if (displayMonth) {
      setCurrentDate(displayMonth)
    }
  }, [displayMonth])

  const handleMonthChange = useCallback(
    (searchCondition: { year: number; month: number }) => {
      const newDate = new DateVO(
        `${searchCondition.year}-${searchCondition.month}-01`
      )
      setCurrentDate(newDate)
      onMonthChange(searchCondition.year, searchCondition.month)
    },
    [onMonthChange]
  )

  const handlePreviousMonth = useCallback(() => {
    const prevMonth = currentDate.subtractMonths(1)
    setCurrentDate(prevMonth)
    onMonthChange(prevMonth.getYear(), prevMonth.getMonth())
  }, [currentDate, onMonthChange])

  const handleNextMonth = useCallback(() => {
    const nextMonth = currentDate.addMonths(1)
    setCurrentDate(nextMonth)
    onMonthChange(nextMonth.getYear(), nextMonth.getMonth())
  }, [currentDate, onMonthChange])

  const handleThisMonth = useCallback(() => {
    const thisMonth = DateVO.now()
    setCurrentDate(thisMonth)
    onMonthChange(thisMonth.getYear(), thisMonth.getMonth())
  }, [onMonthChange])

  return (
    <HeaderContainer>
      <TopContainer>
        <SummaryTitleRow>
          <SummaryTitle>
            {intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.summaryTitle',
            })}
          </SummaryTitle>
          <SummaryActionGroup>
            {isClosing ? (
              <>
                <ProjectStatusIndicator>
                  <LockIcon />
                  <ButtonLabelText>
                    {intl.formatMessage({ id: 'project.closed' })}
                    {closedAt && (
                      <ClosedAtText>
                        {new DateVO(closedAt).format('YYYY/M/D')}
                      </ClosedAtText>
                    )}
                  </ButtonLabelText>
                </ProjectStatusIndicator>
                <CancelLink onClick={onClickProjectClosing}>
                  {intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.projectClosingCancelTitle',
                  })}
                </CancelLink>
              </>
            ) : (
              <ProjectClosingButton onClick={onClickProjectClosing}>
                <LockOpenIcon />
                <ButtonLabelText>
                  {intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.projectClosing',
                  })}
                </ButtonLabelText>
              </ProjectClosingButton>
            )}
          </SummaryActionGroup>
        </SummaryTitleRow>
        <SummaryTableGroup>
          <SummaryTable>
            <SummaryHeaderTitle>
              {intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.summary.approvalStatus',
              })}
            </SummaryHeaderTitle>
            <SummaryItemContainer>
              <SummaryItem>
                <SummaryHeaderCell>
                  {intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.summary.unapproved',
                  })}
                </SummaryHeaderCell>
                <SummaryDataCell>
                  {approvalSummary?.unApproved ?? '0'}人
                </SummaryDataCell>
              </SummaryItem>
              <SummaryItemDivider />
              <SummaryItem>
                <SummaryHeaderCell>
                  {intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.summary.unrequested',
                  })}
                </SummaryHeaderCell>
                <SummaryDataCell>
                  {approvalSummary?.unRequested ?? '0'}人
                </SummaryDataCell>
              </SummaryItem>
            </SummaryItemContainer>
          </SummaryTable>
          <SummaryTable>
            <SummaryHeaderTitle>
              {intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.summary.notEnteredCount',
              })}
            </SummaryHeaderTitle>
            <SummaryItemContainer>
              <SummaryItem>
                <SummaryHeaderCell>
                  {intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.summary.workingHours',
                  })}
                </SummaryHeaderCell>
                <SummaryDataCell>
                  {approvalSummary?.actualWorkHourEmpty ?? '0'}人
                </SummaryDataCell>
              </SummaryItem>
              <SummaryItemDivider />
              <SummaryItem>
                <SummaryHeaderCell>
                  {intl.formatMessage({
                    id: 'taskActualWorkApprovalStatus.summary.taskHours',
                  })}
                </SummaryHeaderCell>
                <SummaryDataCell>
                  {approvalSummary?.taskActualWorkEmpty ?? '0'}人
                </SummaryDataCell>
              </SummaryItem>
            </SummaryItemContainer>
          </SummaryTable>
          <SummaryTable>
            <SummaryHeaderTitle>
              {intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.numberOfMismatches',
              })}
            </SummaryHeaderTitle>
            <SummaryItem>
              <SummaryHeaderCell>
                {intl.formatMessage({
                  id: 'taskActualWorkApprovalStatus.summary.workingTaskHours',
                })}
              </SummaryHeaderCell>
              <SummaryDataCell>
                {approvalSummary?.workingTaskHoursMismatch ?? '0'}人
              </SummaryDataCell>
            </SummaryItem>
          </SummaryTable>
        </SummaryTableGroup>
      </TopContainer>
      <BottomContainer>
        <Button onClick={refresh} disabled={loading} sx={Reload}>
          <ReloadIcon />
        </Button>
        <MonthNavigation>
          <MonthNavigationButtons>
            <ShiftButton onClick={handlePreviousMonth} disabled={loading}>
              <ArrowBackIos style={arrowIconStyle} />
            </ShiftButton>

            <MonthSelect
              year={currentDate.getYear()}
              month={currentDate.getMonth()}
              search={handleMonthChange}
            />

            <ShiftButton onClick={handleNextMonth} disabled={loading}>
              <ArrowForwardIos style={arrowIconStyle} />
            </ShiftButton>

            <ThisMonthButton onClick={handleThisMonth} disabled={loading}>
              {intl.formatMessage({ id: 'this.month' })}
            </ThisMonthButton>
          </MonthNavigationButtons>
        </MonthNavigation>
        <Typography sx={{ color: colorPalette.monotone[5] }}>
          {intl.formatMessage({
            id: 'taskActualWorkApprovalStatus.periodEndDate',
          })}
          ：
        </Typography>
        {onEndDateChange && (
          <EndDateSelector
            value={endDate}
            onChange={onEndDateChange}
            preset={endDatePreset}
            onPresetChange={setEndDatePreset}
            disabled={loading || !isCurrentMonth}
            targetMonth={currentDate}
          />
        )}
        <DecimalAndSexagesimalScaleToggleButtonGroup
          value={decimalScaleUnit}
          onChange={onChangeDecimalScaleUnit}
        />

        {filteredColumns && onDeleteFilteredColumn && resetFilters && (
          <FilterButton
            filteredColumns={filteredColumns}
            onDeleteFilteredColumn={onDeleteFilteredColumn}
            resetFilters={resetFilters}
          />
        )}

        {sortColumnsState &&
          onDeleteSortedColumn &&
          onDeleteSortedAllColumns &&
          onChangeSortColumnState && (
            <SortButton
              onDeleteSortedColumn={onDeleteSortedColumn}
              onDeleteSortedAllColumns={onDeleteSortedAllColumns}
              onChangeSortColumnState={onChangeSortColumnState}
              sortColumnsState={sortColumnsState}
            />
          )}

        {onClickColumnSettingButton && (
          <div style={{ marginTop: '4px' }}>
            <ColumnSettingButton
              onClick={e => onClickColumnSettingButton(e)}
              open={columnSettingOpen}
            />
          </div>
        )}

        <IconGroup>
          <SettingSpeedDial
            onClickExport={onClickExport}
            rowHeight={rowHeight}
            onClickChangeRowHeight={onChangeRowHeight}
          />
        </IconGroup>
      </BottomContainer>
    </HeaderContainer>
  )
}

const HeaderContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  margin: '0px 10px',
  minHeight: '235px',
  gap: '20px',
})

const TopContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '16px',
  padding: '20px',
  backgroundColor: colorPalette.monotone[0],
})

const BottomContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const Reload = {
  background: colorPalette.monotone[1],
  border: `1px solid ${colorPalette.monotone[3]}`,
  borderRadius: '50%',
  height: '28px',
  width: '28px',
  padding: '5px 8px',
  cursor: 'pointer',
  '&:hover': { background: colorPalette.monotone[1] },
}

const SummaryTitleRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: '12px',
})

const SummaryTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: '700',
  color: colorPalette.monotone[10],
})

const SummaryActionGroup = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const SummaryTableGroup = styled('div')({
  display: 'flex',
  gap: '12px',
})

const SummaryTable = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
})

const SummaryItemContainer = styled('div')({
  display: 'flex',
  gap: '12px',
})

const SummaryItemDivider = styled(props => (
  <Divider orientation="vertical" {...props} />
))({
  borderColor: colorPalette.monotone[2],
})

const SummaryItem = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
})

const SummaryHeaderTitle = styled('span')({
  fontSize: '13px',
  lineHeight: 1,
  color: colorPalette.monotone[10],
})

const SummaryHeaderCell = styled('span')({
  fontSize: '10px',
  color: colorPalette.monotone[4],
  lineHeight: 1,
  padding: '2px 8px',
})

const SummaryDataCell = styled('span')({
  fontSize: '14px',
  fontWeight: '700',
  lineHeight: 1,
  color: colorPalette.monotone[10],
  padding: '2px 8px',
})

const MonthNavigation = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const MonthNavigationButtons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

const ShiftButton = styled(IconButton)({
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  width: '24px',
  height: '24px',
  padding: '4px',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const ProjectStatusIndicator = styled('div')({
  backgroundColor: colorPalette.pink[0],
  border: `1px solid ${colorPalette.pink[1]}`,
  borderRadius: '4px',
  padding: '8px 12px',
  fontWeight: 'bold',
  height: '32px',
  fontSize: '12px',
  display: 'flex',
  color: colorPalette.pink[7],
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  gap: '10px',
})

const ThisMonthButton = styled(Button)({
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  minWidth: '48px',
  height: '28px',
  fontSize: '12px',
  color: colorPalette.monotone[4],
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const arrowIconStyle = {
  color: colorPalette.monotone[5],
  fontSize: '14px',
}

const ProjectClosingButton = styled(Button)({})

const ClosedAtText = styled('span')({
  marginLeft: '8px',
  fontSize: '10px',
})

const CancelLink = styled('span')({
  fontSize: '12px',
  color: colorPalette.monotone[5],
  textDecoration: 'underline',
  cursor: 'pointer',
  marginLeft: '4px',
  '&:hover': {
    color: colorPalette.monotone[6],
  },
})

const ButtonLabelText = styled('span')({
  lineHeight: 1,
})

const LockIcon = styled(LockRoundedIcon)({
  fontSize: 16,
  color: colorPalette.pink[5],
})

export default Header
